import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions,
  Button, Chip, Avatar, Divider, CircularProgress, Alert,
  Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Event as EventIcon,
  TrendingUp as TrendingUpIcon,
  PersonAdd as PersonAddIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { hasPermission } from '../../../../utils/permissionUtils';
import { EMPLOYEE_STATUS_COLORS, formatEnumLabel } from '../../../../constants/hrm';
import { useHRMDashboard } from '../../../../hooks/useHRM';

const eventTypeColors = {
  HIRED: 'success',
  PROMOTED: 'info',
  TRANSFERRED: 'primary',
  TERMINATED: 'error',
  RESIGNED: 'warning',
  ON_LEAVE: 'warning',
  RETURNED: 'success',
  PROBATION_END: 'info',
};

const HRMDashboard = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    document.title = 'HRM | KlonIT Web Application';
    const checkPermissions = async () => {
      try {
        const view = await hasPermission('hrm', 'view');
        const modify = await hasPermission('hrm', 'add|edit');
        setCanView(view);
        setCanModify(modify);
      } catch (err) {
        console.error('Error checking permissions:', err);
      } finally {
        setPermissionsLoaded(true);
      }
    };
    checkPermissions();
  }, []);

  // React Query hook for dashboard data
  const { data: dashboardResponse, isLoading: dashboardLoading } = useHRMDashboard(chatbotId, { enabled: !!chatbotId && canView });
  const dashboardData = dashboardResponse?.data || null;
  const loading = !permissionsLoaded || dashboardLoading;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view the HRM module.
      </Alert>
    );
  }

  const {
    total_employees = 0,
    employees_by_status = {},
    employees_by_employment_type: employees_by_type = {},
    total_departments: department_count = 0,
    total_positions: position_count = 0,
    recent_lifecycle_events = [],
  } = dashboardData || {};

  const cards = [
    {
      title: 'Employees',
      description: 'Manage your employee directory with detailed profiles, departments, and positions.',
      icon: <PeopleIcon sx={{ fontSize: 60, color: '#1976d2' }} />,
      count: total_employees,
      link: `/chatbot/${chatbotId}/hrm/employees`,
      color: '#1976d2',
    },
    {
      title: 'Departments',
      description: 'Organize your company structure with hierarchical department management.',
      icon: <BusinessIcon sx={{ fontSize: 60, color: '#2e7d32' }} />,
      count: department_count,
      link: `/chatbot/${chatbotId}/hrm/departments`,
      color: '#2e7d32',
    },
    {
      title: 'Positions',
      description: 'Define job positions and designations with salary grades and requirements.',
      icon: <WorkIcon sx={{ fontSize: 60, color: '#ed6c02' }} />,
      count: position_count,
      link: `/chatbot/${chatbotId}/hrm/positions`,
      color: '#ed6c02',
    },
    {
      title: 'Lifecycle Events',
      description: 'Track employee lifecycle including onboarding, promotions, and separations.',
      icon: <EventIcon sx={{ fontSize: 60, color: '#9c27b0' }} />,
      count: recent_lifecycle_events.length,
      link: `/chatbot/${chatbotId}/hrm/lifecycle`,
      color: '#9c27b0',
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            HRM
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Human Resource Management
          </Typography>
        </Box>
      </Box>

      {/* Main Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: '0.3s',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                },
              }}
              onClick={() => navigate(card.link)}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: `${card.color}15`,
                    margin: '0 auto 16px',
                  }}
                >
                  {card.icon}
                </Avatar>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  {card.title}
                </Typography>
                <Typography variant="h3" color={card.color} fontWeight={700} sx={{ mb: 2 }}>
                  {card.count.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ bgcolor: card.color }}
                >
                  Manage
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Employee Breakdown and Recent Events */}
      <Grid container spacing={3}>
        {/* Employee Breakdown */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Employee Breakdown
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              By Status
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
              {Object.entries(employees_by_status).map(([status, count]) => (
                <Chip
                  key={status}
                  label={`${formatEnumLabel(status)}: ${count}`}
                  color={EMPLOYEE_STATUS_COLORS[status] || 'default'}
                  size="small"
                  sx={{ mb: 1 }}
                />
              ))}
              {Object.keys(employees_by_status).length === 0 && (
                <Typography variant="body2" color="text.secondary">No employee data available</Typography>
              )}
            </Stack>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              By Employment Type
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {Object.entries(employees_by_type).map(([type, count]) => (
                <Chip
                  key={type}
                  label={`${formatEnumLabel(type)}: ${count}`}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 1 }}
                />
              ))}
              {Object.keys(employees_by_type).length === 0 && (
                <Typography variant="body2" color="text.secondary">No employee data available</Typography>
              )}
            </Stack>

            {canModify && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => navigate(`/chatbot/${chatbotId}/hrm/employees?action=create`)}
                >
                  Add New Employee
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Lifecycle Events */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                Recent Lifecycle Events
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate(`/chatbot/${chatbotId}/hrm/lifecycle`)}
              >
                View All
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {recent_lifecycle_events.length > 0 ? (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recent_lifecycle_events.slice(0, 5).map((event) => (
                      <TableRow key={event.id} hover>
                        <TableCell>
                          {event.employee_first_name} {event.employee_last_name}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatEnumLabel(event.event_type)}
                            color={eventTypeColors[event.event_type] || 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {event.event_date
                            ? new Date(event.event_date).toLocaleDateString()
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No recent lifecycle events
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      {canModify && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Quick Actions
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/hrm/employees?action=create`)}
            >
              New Employee
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<BusinessIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/hrm/departments`)}
            >
              Manage Departments
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<WorkIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/hrm/positions`)}
            >
              Manage Positions
            </Button>
            <Button
              variant="outlined"
              startIcon={<EventIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/hrm/lifecycle`)}
            >
              Lifecycle Events
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default HRMDashboard;
